// src/tattoo/TattooApp.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSnapshot } from "valtio";
import {
  tattoo,
  addTextLayer,
  updateTextLayer,
  mergeLayerDown,
  scrubDeadBlobLayers,
  setMorph,
  goDesign,
  clearAll,
  getProjectJSON,
  getCurrentProjectId,
  loadProjectFromData,
  resetProjectToDefaults,
  addDecalFromUrl,
  addDecalFromFile,
} from "./state";
import {
  enableAutosave,
  installBeforeUnload,
  markSaved,
  restoreAutosaveIfAvailable,
  AUTOSAVE_KEY,
} from "./autosave.js";
import "./tattoo.css";
import LeftPanel from "./LeftPanel.jsx";
import PreviewPane from "./PreviewPane.jsx";
import useMediaQuery from "./useMediaQuery";
import "./tattoo-ui.css";
import TattooShareInit from "./TattooShareInit.jsx";
import SaveShareButton from "./SaveShareButton.jsx";
import AuthGate from "./components/AuthGate.jsx";
import BillingReturnHandler from "./billing/BillingReturnHandler.jsx";
import TextLayerDialog from "./components/TextLayerDialog.jsx";
import { supabase } from "./lib/supabaseClient.js";
import Dashboard from "./Dashboard.jsx";
import {
  upsertRecentProject,
  cleanupOldSnapshots,
  SNAPSHOT_KEY_PREFIX,
} from "./projectsIndex";
import LZString from "lz-string";
import { guidePaintRef } from "./GuidePaint"; // adjust path if needed
import { unresolvedCount } from "../comments/commentsState";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { readPendingInquiry } from "./lib/flashUtils";
import { idbPut, idbGet } from "./idbAutosave";
import { useLocation, useNavigate } from "react-router-dom";

const FEEDBACK_NUDGE_KEY = "tt_seen_feedback_nudge_v2"; // bump version to re-show everywhere

function FeedbackNudgePortal({ effectiveReadOnly, isMobile, leftOpen }) {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    if (!effectiveReadOnly || !isMobile || leftOpen) return;

    let seen = false;
    try {
      seen = localStorage.getItem(FEEDBACK_NUDGE_KEY) === "1";
    } catch {}
    if (seen) return;

    const t = setTimeout(() => setShow(true), 600);

    return () => {
      clearTimeout(t);
    };
  }, [effectiveReadOnly, isMobile, leftOpen]);

  if (!show) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(64px + env(safe-area-inset-bottom, 0px))",
        zIndex: 2147483647,
        maxWidth: "86vw",
        padding: "10px 12px",
        borderRadius: 12,
        background: "rgba(18,18,18,0.92)",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "#f5f5f5",
        fontSize: 13,
        lineHeight: 1.25,
        boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
        backdropFilter: "blur(6px)",
        pointerEvents: "none",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>What do you think?</div>
      <div style={{ opacity: 0.9 }}>
        Tap <b>Feedback</b> to leave a note 👇
      </div>

      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%) rotate(45deg)",
          bottom: -8,
          width: 14,
          height: 14,
          background: "rgba(18,18,18,0.92)",
          borderBottom: "1px solid rgba(255,255,255,0.14)",
        }}
      />
    </div>,
    document.body
  );
}

function MergeConfirmDialog({ snap }) {
  const mc = snap.ui?.mergeConfirm;
  if (!mc?.open) return null;

  function close() {
    tattoo.ui.mergeConfirm.open = false;
  }

  function confirm() {
    tattoo.undo.lastMerge = {
      layers: tattoo.layers.map((L) => ({
        ...L,
        // clone nested objects you know exist
        transform: L.transform ? { ...L.transform } : undefined,
        placement: L.placement ? { ...L.placement } : undefined,
        // add others if you have them:
        // uv: L.uv ? { ...L.uv } : undefined,
        // tweaks: L.tweaks ? { ...L.tweaks } : undefined,
      })),
    };

    mergeLayerDown(mc.layerId, mc.partKey);
    console.log(
      "merge target",
      tattoo.layers.map((l) => l.id)
    );
    tattoo.ui.mergeConfirm.open = false;
    tattoo.ui.showUndoMerge = true;

    setTimeout(() => {
      tattoo.ui.showUndoMerge = false;
    }, 5000);
  }

  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>Merge layers?</h3>
        <p>This will bake this layer into the one below.</p>

        <div className="modal-actions">
          <button className="btn-ghost" onClick={close}>
            Cancel
          </button>
          <button className="btn btn-danger" onClick={confirm}>
            Merge
          </button>
        </div>
      </div>
    </div>
  );
}

function UndoMergeToast({ snap }) {
  if (!snap.ui?.showUndoMerge) return null;

  return (
    <div className="undo-toast">
      <span>Layers merged</span>
      <button
        onClick={() => {
          if (!tattoo.undo?.lastMerge) return;
          tattoo.layers = tattoo.undo.lastMerge.layers;
          tattoo.undo.lastMerge = null;
          tattoo.ui.showUndoMerge = false;
        }}
      >
        Undo
      </button>
    </div>
  );
}

export default function TattooApp({ readOnly = false }) {
  const snap = useSnapshot(tattoo);
  const isMobile = useMediaQuery("(max-width: 820px)");
  const location = useLocation();
  const warnedLsQuotaRef = React.useRef(false);
  const snapshotInFlightRef = useRef(false);
  const LS_LAST_SCREEN = "tt:last_screen"; // "lounge" | "studio"
  const LS_LAST_PID = "tt:last_project_id"; // project id string

  const allowGuestStudioEntry = isGuestArtistFlashEntry();

  const previewLayerIdRef = useRef(null);
  const previewRequestRef = useRef(0);

  const [panelMode, setPanelMode] = React.useState("layers"); // "layers" | "morphs" | whatever
  const openMorphs = React.useCallback(() => {
    setPanelMode("morphs");
    if (isMobile) setSheetOpen();
  }, [isMobile]);

  // ✅ add back (must be above any useEffect / render that references it)
  // viewer route?
  const isViewerRoute = location.pathname?.startsWith("/view");

  // ✅ UI-level read-only
  const effectiveReadOnly = !!(readOnly || tattoo.readOnly || isViewerRoute);

  const [openingDashboard, setOpeningDashboard] = useState(false);

  const SNAP_IDB_KEY = (projectId) => `__snapshot__:${projectId}`;

  const [showDashboard, setShowDashboard] = useState(true);

  // --------------------
  // Mobile bottom sheet (HEIGHT-DRIVEN)
  // --------------------
  const sheetRef = React.useRef(null);
  const handleRef = React.useRef(null);

  const SHEET_MIN_PX = 72; // closed "peek" height
  const SHEET_MAX_RATIO = 0.8; // max open = 50% viewport (you asked for this)
  const SHEET_SNAP_PX = 18; // soft snap threshold
  const SHEET_OPEN_RATIO = 0.5; // tap-open target

  const [sheetH, setSheetH] = React.useState(() => SHEET_MIN_PX);
  const sheetHRef = React.useRef(sheetH);

  const navigate = useNavigate();

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestDraft, setRequestDraft] = useState(null);

  React.useEffect(() => {
    sheetHRef.current = sheetH;
  }, [sheetH]);

  const dragRef = React.useRef({
    active: false,
    startY: 0,
    startH: 0,
    maxH: 0,
  });

  function getViewportH() {
    // iOS-friendly: visualViewport is the truth when browser bars change
    return Math.round(window.visualViewport?.height || window.innerHeight || 0);
  }

  function getMaxH() {
    const vh = getViewportH();
    return Math.max(SHEET_MIN_PX, Math.round(vh * SHEET_MAX_RATIO));
  }

  function getOpenH() {
    const vh = getViewportH();
    return Math.max(SHEET_MIN_PX, Math.round(vh * SHEET_OPEN_RATIO));
  }

  function setSheetOpen() {
    setSheetH(getOpenH());
  }

  function setSheetClosed() {
    setSheetH(SHEET_MIN_PX);
  }

  const isSheetClosed = React.useMemo(
    () => sheetH <= SHEET_MIN_PX + 2,
    [sheetH]
  );

  const toggleDesignPanel = React.useCallback(() => {
    if (!isMobile) {
      tattoo.leftOpen = !tattoo.leftOpen;
      return;
    }
    if (sheetHRef.current <= SHEET_MIN_PX + 2) setSheetOpen();
    else setSheetClosed();
  }, [isMobile]);

  function onSheetPointerDown(e) {
    if (!isMobile) return;
    dragRef.current.active = true;
    dragRef.current.startY = e.clientY;
    dragRef.current.startH = sheetHRef.current;
    dragRef.current.maxH = getMaxH();

    sheetRef.current?.classList.add("is-dragging");

    try {
      handleRef.current?.setPointerCapture?.(e.pointerId);
    } catch {}

    e.preventDefault();
    e.stopPropagation();
  }

  function onSheetPointerMove(e) {
    if (!dragRef.current.active) return;

    const dy = e.clientY - dragRef.current.startY;
    // drag DOWN => smaller height, drag UP => bigger height
    let next = dragRef.current.startH - dy;
    next = Math.max(SHEET_MIN_PX, Math.min(dragRef.current.maxH, next));

    setSheetH(next);

    e.preventDefault();
  }

  function onSheetPointerUp(e) {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;

    sheetRef.current?.classList.remove("is-dragging");

    const maxH = getMaxH();
    const h = sheetHRef.current;

    // ✅ soft snap only when close to ends
    if (Math.abs(h - SHEET_MIN_PX) <= SHEET_SNAP_PX) setSheetH(SHEET_MIN_PX);
    else if (Math.abs(h - maxH) <= SHEET_SNAP_PX) setSheetH(maxH);
    // otherwise keep the user's custom height

    try {
      handleRef.current?.releasePointerCapture?.(e.pointerId);
    } catch {}
  }

  // keep proxy in sync (optional, but your app expects it)
  React.useEffect(() => {
    if (!isMobile) return;
    tattoo.leftOpen = !isSheetClosed;
  }, [isMobile, isSheetClosed]);

  // clamp on viewport changes (rotation / address bar)
  React.useEffect(() => {
    if (!isMobile) return;

    const clampNow = () => {
      const maxH = getMaxH();
      setSheetH((h) => Math.max(SHEET_MIN_PX, Math.min(maxH, h)));
    };

    window.addEventListener("resize", clampNow);
    window.visualViewport?.addEventListener("resize", clampNow);

    return () => {
      window.removeEventListener("resize", clampNow);
      window.visualViewport?.removeEventListener("resize", clampNow);
    };
  }, [isMobile]);

  function rememberStudioOpened(projectId) {
    try {
      localStorage.setItem(LS_LAST_SCREEN, "studio");
      if (projectId) localStorage.setItem(LS_LAST_PID, projectId);
    } catch {}
  }

  function rememberLoungeOpened() {
    try {
      localStorage.setItem(LS_LAST_SCREEN, "lounge");
    } catch {}
  }

  const [currentUser, setCurrentUser] = useState(null);

  // ref to talk to PreviewPane (for snapshots)
  const previewRef = useRef(null);

  const didAutoCloseRef = useRef(false);

  // image URLs we’ll send to the artist (start as placeholder)
  const [renderedImageUrls, setRenderedImageUrls] = useState([
    "https://placehold.co/400x400?text=Tattoo+Preview",
  ]);

  function isGuestArtistFlashEntry() {
    try {
      const qp = new URLSearchParams(window.location.search);
      return qp.get("guest") === "1" && qp.get("from") === "artist-flash";
    } catch {
      return false;
    }
  }

  function parseSizeInches(sizeStr) {
    if (!sizeStr || typeof sizeStr !== "string") return null;

    // Normalize en-dash/em-dash to "-"
    const s = sizeStr.replace(/[–—]/g, "-").toLowerCase();

    // Extract first two numbers
    const nums = s.match(/(\d+(\.\d+)?)/g);
    if (!nums || nums.length === 0) return null;

    const a = parseFloat(nums[0]);
    const b = nums.length > 1 ? parseFloat(nums[1]) : a;

    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

    // average if range, otherwise single value
    return (a + b) * 0.5;
  }

  function inchesToInitialScale(inches) {
    // Tune these once by feel:
    // - 3–4 in should land around "medium" on torso/arm
    const refIn = 6; // “6 inches” feels like a big-ish decal
    const refScale = 0.11;

    const s = (Number(inches) / refIn) * refScale;
    return Math.max(0.03, Math.min(0.9, s));
  }

  // Lock html/body scroll while the editor is mounted. The editor uses a
  // fixed-viewport canvas + side panels and breaks visually if the page
  // scrolls. Scoping the lock here (rather than in a global CSS rule) lets
  // every other route — inbox, billing, public artist pages — scroll
  // normally. Restored on unmount so navigating away unlocks scroll.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("tt_pending_flash");
      if (!raw) return;

      // If flash is pending, we must show Studio so the import can happen
      setShowDashboard(false);
    } catch {}
  }, []);

  // Pending inquiry hand-off from the public artist page (set by
  // stashPendingInquiry on /artists/<slug>'s "Start your design" CTA).
  // Skip the dashboard, drop the user straight into the editor, and show
  // a guidance toast naming the destination shop so the inquiry context
  // survives the navigation hop. Toast announces once per browser tab
  // (sessionStorage), so a refresh inside the same session doesn't nag.
  // The pending-inquiry payload itself is NOT cleared here — a follow-up
  // pass will use it to pre-select the artist in TestLeadForm. It auto-
  // expires after 30 minutes (see readPendingInquiry).
  useEffect(() => {
    const inq = readPendingInquiry();
    if (!inq) return;

    setShowDashboard(false);

    const announceKey = `tt_inquiry_announced_${inq.ts}`;
    try {
      if (sessionStorage.getItem(announceKey)) return;
      sessionStorage.setItem(announceKey, "1");
    } catch {
      // sessionStorage unavailable — fall through and announce anyway.
    }

    toast(
      `Designing for ${inq.shopName} — open "Send to artist" in the left panel when your design is ready.`,
      { duration: 10000 }
    );
  }, []);

  // viewer route?
  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const flashUrl = qp.get("flash");
    const flashName = qp.get("flash_name");

    if (!flashUrl) return;

    (async () => {
      try {
        // This assumes you have a helper that can add by URL.
        // If you ONLY have addLayerFromFile, tell me and I’ll adapt it.
        await addDecalFromFile(null, {
          url: flashUrl,
          name: flashName || "Flash",
          isDecal: true,
          source: "flash",
          ownerShopId: null,
          ownerShopName: null,
          ownerShopSlug: null,
        });

        // clean the URL so refresh doesn't re-add it
        qp.delete("flash");
        qp.delete("flash_name");
        navigate(
          {
            pathname: location.pathname,
            search: qp.toString() ? `?${qp}` : "",
          },
          { replace: true }
        );
      } catch (e) {
        console.warn("[flash] failed to add flash layer:", e);
      }
    })();
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    async function importPendingFlash() {
      console.log(
        "[debug] pending_request at import:",
        localStorage.getItem("tt_pending_request")
      );

      let payload = null;

      try {
        const raw = localStorage.getItem("tt_pending_flash");
        if (!raw) return;

        localStorage.removeItem("tt_pending_flash");
        payload = JSON.parse(raw);
        console.log("[flash] pending payload at app", payload);
      } catch (e) {
        console.warn("[flash] bad pending payload", e);
        try {
          localStorage.removeItem("tt_pending_flash");
        } catch {}
        return;
      }

      const url = payload?.url;
      const name = payload?.name || "Flash";
      if (!url) return;

      try {
        const res = await fetch(url, { mode: "cors" });
        const blob = await res.blob();

        const ext =
          blob.type && blob.type.includes("png")
            ? "png"
            : blob.type && blob.type.includes("jpeg")
            ? "jpg"
            : "png";

        const file = new File([blob], `${name}.${ext}`, {
          type: blob.type || "image/png",
        });

        if (cancelled) return;

        const inches = parseSizeInches(payload?.size);
        const initialScale = inches ? inchesToInitialScale(inches) : undefined;

        console.log("[flash] size -> scale", {
          size: payload?.size,
          inches,
          initialScale,
        });

        setShowDashboard(false);
        try {
          goDesign();
        } catch {}
        console.log("[flash import] owner meta before addDecalFromFile", {
          ownerShopId: payload?.ownerShopId,
          ownerShopName: payload?.ownerShopName,
          ownerShopSlug: payload?.ownerShopSlug,
        });
        await addDecalFromFile(file, {
          name,
          source: "flash",
          initialScale,
          ownerShopId: payload?.ownerShopId || null,
          ownerShopName: payload?.ownerShopName || null,
          ownerShopSlug: payload?.ownerShopSlug || null,
        });
        const added = tattoo.layers[tattoo.layers.length - 1];

        console.log("[flash add] explicit layer meta", {
          name: added?.name,
          source: added?.source,
          ownerShopId: added?.ownerShopId,
          ownerShopName: added?.ownerShopName,
          ownerShopSlug: added?.ownerShopSlug,
        });

        try {
          const rawReq = localStorage.getItem("tt_pending_request");
          if (rawReq) {
            localStorage.removeItem("tt_pending_request");

            const req = JSON.parse(rawReq);

            const requestPayload = {
              kind: "flash_request",
              createdAt: new Date().toISOString(),
              flash: { name, url },
              requestIntent: req,
            };

            setRequestDraft(requestPayload);
            setRequestOpen(true);
          }
        } catch (e) {
          console.warn("[request] failed to build request payload", e);
          try {
            localStorage.removeItem("tt_pending_request");
          } catch {}
        }
      } catch (e) {
        console.warn("[flash] failed to import", e);
      }
    }

    function handlePendingFlashEvent() {
      importPendingFlash();
    }

    importPendingFlash();
    window.addEventListener("tt-pending-flash", handlePendingFlashEvent);

    return () => {
      cancelled = true;
      window.removeEventListener("tt-pending-flash", handlePendingFlashEvent);
    };
  }, []);

  async function exportPreviewImage(opts = {}) {
    if (!previewRef.current || !previewRef.current.captureExportPng) {
      console.warn(
        "[TattooApp] exportPreviewImage: no preview export available"
      );
      return null;
    }

    try {
      return await previewRef.current.captureExportPng({
        width: 2048,
        height: 2048,
        format: "image/png",
        ...opts,
      });
    } catch (err) {
      console.warn("[TattooApp] exportPreviewImage failed:", err);
      return null;
    }
  }

  async function exportPreviewVideo(opts = {}) {
    if (!previewRef.current || !previewRef.current.exportVideoSlowPush) {
      console.warn(
        "[TattooApp] exportPreviewVideo: no preview video export available"
      );
      return null;
    }

    try {
      return await previewRef.current.exportVideoSlowPush({
        durationMs: 5000,
        fps: 30,
        ...opts,
      });
    } catch (err) {
      console.warn("[TattooApp] exportPreviewVideo failed:", err);
      return null;
    }
  }

  useEffect(() => {
    async function handlePreview(e) {
      const item = e.detail;
      const requestId = ++previewRequestRef.current;

      // always remove any existing preview first
      tattoo.layers = tattoo.layers.filter((l) => !l.isPreview);
      previewLayerIdRef.current = null;

      // if hover ended, we're done
      if (!item) return;

      try {
        const res = await fetch(item.url, { mode: "cors" });
        const blob = await res.blob();

        // if another hover event happened while we were loading, abandon this one
        if (requestId !== previewRequestRef.current) return;

        const ext =
          blob.type && blob.type.includes("png")
            ? "png"
            : blob.type && blob.type.includes("jpeg")
            ? "jpg"
            : "png";

        const file = new File([blob], `preview.${ext}`, {
          type: blob.type || "image/png",
        });

        const inches = parseSizeInches(item?.size);
        const initialScale = inches ? inchesToInitialScale(inches) : undefined;

        const beforeIds = new Set((tattoo.layers || []).map((l) => l.id));

        await addDecalFromFile(file, {
          name: "Flash preview",
          source: "flash-preview",
          initialScale,
          opacity: 0.35,
          isPreview: true,
        });

        // if another hover event happened while we were adding, clean this one up
        if (requestId !== previewRequestRef.current) {
          tattoo.layers = tattoo.layers.filter((l) => !l.isPreview);
          previewLayerIdRef.current = null;
          return;
        }

        const addedLayer = (tattoo.layers || []).find(
          (l) => !beforeIds.has(l.id) && l.isPreview
        );

        if (addedLayer) {
          previewLayerIdRef.current = addedLayer.id;
        } else {
          // fallback: remove any preview layers if we couldn't identify cleanly
          const previews = (tattoo.layers || []).filter((l) => l.isPreview);
          if (previews.length) {
            previewLayerIdRef.current = previews[previews.length - 1].id;
          }
        }
      } catch (e) {
        console.warn("[preview] failed", e);
      }
    }

    return () => {
      if (previewLayerIdRef.current) {
        tattoo.layers = tattoo.layers.filter(
          (l) => l.id !== previewLayerIdRef.current
        );
        previewLayerIdRef.current = null;
      }
    };
  }, []);

  // ---------- Auth wiring (single source of truth) ----------
  useEffect(() => {
    let isMounted = true;

    supabase.auth
      .getUser()
      .then(({ data, error }) => {
        if (!isMounted) return;
        if (error) {
          console.warn("[TattooApp] getUser error:", error);
          setCurrentUser(null);
        } else {
          setCurrentUser(data?.user ?? null);
        }
      })
      .catch((e) => {
        if (!isMounted) return;
        console.warn("[TattooApp] getUser threw:", e);
        setCurrentUser(null);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;
      setCurrentUser(session?.user ?? null);
    });

    return () => {
      isMounted = false;
      try {
        subscription?.unsubscribe?.();
      } catch {}
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // If URL is explicitly /view, never restore studio
      if (isViewerRoute) return;

      // detect reload
      let navType = "";
      try {
        navType = performance.getEntriesByType("navigation")[0]?.type || "";
      } catch {}
      const isReload = navType === "reload";

      let lastScreen = null;
      let lastPid = null;

      // ✅ If we're coming in from an artist flash entry or already have
      // a pending flash import, do NOT lounge/dashboard.
      // Let the app boot normally so the import effect can run.
      let hasPendingFlash = false;
      try {
        hasPendingFlash = !!localStorage.getItem("tt_pending_flash");
      } catch {}

      let isGuestArtistFlashEntry = false;
      try {
        const qp = new URLSearchParams(window.location.search);
        isGuestArtistFlashEntry =
          qp.get("guest") === "1" && qp.get("from") === "artist-flash";
      } catch {}

      if (hasPendingFlash || isGuestArtistFlashEntry) {
        setShowDashboard(false);

        try {
          goDesign();
        } catch {}

        // clean the URL after artist-flash entry
        try {
          {/* const url = new URL(window.location.href);
          url.searchParams.delete("guest");
          url.searchParams.delete("from");
          url.searchParams.delete("intent");
          window.history.replaceState( 
            {},
            "",
            url.pathname + url.search + url.hash
          );*/}
        } catch {}

        return;
      }

      try {
        lastScreen = localStorage.getItem(LS_LAST_SCREEN);
        lastPid = localStorage.getItem(LS_LAST_PID);
      } catch {}

      // ✅ Always Lounge on normal navigation/login.
      // ✅ Only restore studio on an actual browser reload.
      if (!isReload || lastScreen !== "studio") {
        setShowDashboard(true);
        return;
      }

      // ✅ Attempt to restore the last studio project (AUTHORITATIVE ORDER)
      let loaded = false;

      // 0) AUTOSAVE FIRST (source of truth)
      if (!loaded) {
        try {
          loaded = !!(await restoreAutosaveIfAvailable());
        } catch (e) {
          console.warn("[TattooApp] boot restore: autosave failed", e);
        }
      }

      // 1) IDB snapshot (legacy fallback)
      if (!loaded && lastPid) {
        try {
          const rec = await idbGet(SNAP_IDB_KEY(lastPid));
          const payload = rec?.payload;

          if (payload && typeof payload === "string") {
            const json = LZString.decompressFromUTF16(payload);
            if (json) {
              // Boot restore knows the slot id (lastPid) — pass it so
              // tattoo.projectId is pinned to the right slot before any
              // autosave fires.
              loadProjectFromData(JSON.parse(json), lastPid);
              loaded = true;
            }
          }
        } catch (e) {
          console.warn("[TattooApp] boot restore: IDB snapshot failed", e);
        }
      }

      // 2) localStorage snapshot (legacy fallback)
      if (!loaded && lastPid) {
        try {
          const raw = localStorage.getItem(`${SNAPSHOT_KEY_PREFIX}${lastPid}`);
          if (raw) {
            const data = decodeSnapshot(raw);
            if (data) {
              loadProjectFromData(data, lastPid);
              loaded = true;
            }
          }
        } catch (e) {
          console.warn("[TattooApp] boot restore: LS snapshot failed", e);
        }
      }

      if (cancelled) return;

      if (loaded) {
        // ✅ restore succeeded → mark studio as last known state
        try {
          const pid = getCurrentProjectId?.() || lastPid;
          if (pid) rememberStudioOpened(pid);
        } catch {}

        setShowDashboard(false);
      } else {
        // ❌ restore failed → lounge, and clear pointers so we stop retrying forever
        setShowDashboard(true);
        try {
          localStorage.removeItem(LS_LAST_SCREEN);
          localStorage.removeItem(LS_LAST_PID);
        } catch {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isViewerRoute, goDesign]);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const flashUrl = url.searchParams.get("flash");
      const flashName = url.searchParams.get("flash_name") || "flash";

      if (!flashUrl) return;

      // add as decal layer
      addDecalFromUrl(flashUrl, { name: flashName, initialScale: 0.55 });

      // IMPORTANT: remove params so refresh doesn’t re-add
      url.searchParams.delete("flash");
      url.searchParams.delete("flash_name");
      window.history.replaceState({}, "", url.toString());
    } catch (e) {
      console.warn("[flash] failed to auto-add:", e);
    }
  }, []);

  // ---------- Autosave + beforeunload wiring ----------
  const autosaveStopRef = useRef(null);
  const beforeUnloadCleanupRef = useRef(null);

  useEffect(() => {
    // Don’t run autosave in viewer mode, read-only, or while on the dashboard
    if (effectiveReadOnly || showDashboard) {
      // If we previously wired it up, tear it down
      autosaveStopRef.current?.();
      autosaveStopRef.current = null;

      beforeUnloadCleanupRef.current?.();
      beforeUnloadCleanupRef.current = null;

      return;
    }

    console.log("[TattooApp] enabling autosave");
    autosaveStopRef.current = enableAutosave();
    beforeUnloadCleanupRef.current = installBeforeUnload();

    return () => {
      autosaveStopRef.current?.();
      autosaveStopRef.current = null;

      beforeUnloadCleanupRef.current?.();
      beforeUnloadCleanupRef.current = null;
    };
  }, [effectiveReadOnly, showDashboard]);

  // ---- snapshot rate-limit tuning ----
  const SNAPSHOT_MIN_INTERVAL_MS = 4000; // don’t write big snapshot more often than this
  const SNAPSHOT_TRAIL_MS = 900; // if changes keep happening, write once after they settle

  const lastSnapshotAtRef = useRef(0);
  const snapshotTimerRef = useRef(null);
  const snapshotPendingIdRef = useRef(null);

  const lastThumbWriteRef = useRef(Promise.resolve());
  const compositeReadyAtRef = useRef(0);

  function waitForCompositeReady({ timeoutMs = 1400 } = {}) {
    return new Promise((resolve) => {
      const done = () => resolve(true);

      // If we very recently got a composite-ready, don't wait
      if (Date.now() - (compositeReadyAtRef.current || 0) < 250) return done();

      let t = null;
      const on = () => {
        cleanup();
        done();
      };

      const cleanup = () => {
        if (t) clearTimeout(t);
        window.removeEventListener("tattoo:composite-ready", on);
      };

      window.addEventListener("tattoo:composite-ready", on, { once: true });
      t = setTimeout(() => {
        cleanup();
        resolve(false); // timed out, we'll still capture
      }, timeoutMs);
    });
  }

  function scheduleSnapshot(projectId, { force = false } = {}) {
    if (!projectId) return;

    snapshotPendingIdRef.current = projectId;

    const run = async () => {
      if (snapshotInFlightRef.current) return;
      snapshotInFlightRef.current = true;
      try {
        await saveSnapshot(snapshotPendingIdRef.current);
        lastSnapshotAtRef.current = Date.now();
      } finally {
        snapshotInFlightRef.current = false;
      }
    };

    if (snapshotTimerRef.current) {
      clearTimeout(snapshotTimerRef.current);
      snapshotTimerRef.current = null;
    }

    if (force) {
      run();
      return;
    }

    const now = Date.now();
    const since = now - (lastSnapshotAtRef.current || 0);

    if (since >= SNAPSHOT_MIN_INTERVAL_MS) {
      run();
      return;
    }

    const wait = Math.max(SNAPSHOT_TRAIL_MS, SNAPSHOT_MIN_INTERVAL_MS - since);
    snapshotTimerRef.current = setTimeout(run, wait);
  }

  // ---- snapshot warnings / rate-limit guards ----

  useEffect(() => {
    const onReady = () => {
      compositeReadyAtRef.current = Date.now();
    };
    window.addEventListener("tattoo:composite-ready", onReady);
    return () => window.removeEventListener("tattoo:composite-ready", onReady);
  }, []);

  function RequestModal({ draft, onClose }) {
    const [name, setName] = React.useState("");
    const [contact, setContact] = React.useState("");
    const [notes, setNotes] = React.useState("");

    return (
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: 14,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(520px, 100%)",
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(18,18,18,0.98)",
            padding: 14,
            color: "white",
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 16 }}>
            Request this tattoo
          </div>
          <div style={{ marginTop: 6, opacity: 0.85, fontSize: 13 }}>
            Flash: <strong>{draft.flash?.name || "Flash"}</strong>
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name (optional)"
              style={{ padding: "10px 12px", borderRadius: 10 }}
            />
            <input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Email or phone (optional)"
              style={{ padding: "10px 12px", borderRadius: 10 }}
            />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (size, placement tweaks, timing...)"
              rows={3}
              style={{ padding: "10px 12px", borderRadius: 10 }}
            />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <button
              type="button"
              className="tt-btn tt-btn-primary"
              style={{ flex: 1 }}
              onClick={() => {
                const finalPayload = {
                  ...draft,
                  customer: { name, contact },
                  notes,
                };
                console.log("[flash-request]", finalPayload);
                onClose();
              }}
            >
              Send request (log)
            </button>
            <button type="button" className="tt-btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- helper: save JSON snapshot for a project ----------
  async function saveSnapshot(projectId) {
    // 🔎 DEBUG MARKER — proves this deploy has the empty-empty guard.
    // Look for "[TATTUNE-DEBUG] saveSnapshot v2 called" in prod console
    // to confirm the guarded code is actually running. Remove once
    // phantom hunt is done.
    console.warn("[TATTUNE-DEBUG] saveSnapshot v2 called", {
      projectId,
      hasName:
        typeof tattoo.projectName === "string" && !!tattoo.projectName.trim(),
      layerCount: Array.isArray(tattoo.layers) ? tattoo.layers.length : -1,
    });

    // Refuse to write with no slot ID. Without this guard, callers that
    // pass a null projectId would write to "snapshot:null" — a bogus
    // key that pollutes storage and confuses later restores.
    if (!projectId) {
      console.warn(
        "[TattooApp] saveSnapshot called with no projectId — skipping"
      );
      return;
    }

    // 🛡️ Same empty-empty guard as autosave + captureAndIndexPreview.
    // openDash calls saveSnapshot directly, bypassing the other guards
    // — so we need a matching guard here to prevent phantom slots from
    // being written when the user hits "back to dashboard" on an
    // unedited fresh session.
    const nameHasContent =
      typeof tattoo.projectName === "string" && tattoo.projectName.trim();
    const hasLayers = Array.isArray(tattoo.layers) && tattoo.layers.length > 0;
    if (!nameHasContent && !hasLayers) {
      console.warn(
        "[TattooApp] saveSnapshot: SKIPPING — empty-empty state (no name, no layers)",
        { projectId }
      );
      return;
    }

    const lsKey = `${SNAPSHOT_KEY_PREFIX}${projectId}`;

    try {
      const json = getProjectJSON();
      const compressed = LZString.compressToUTF16(json);

      // If we've already seen LS quota fail, skip LS entirely
      if (warnedLsQuotaRef.current) {
        // 🔥 ensure no stale LS snapshot can win later
        try {
          localStorage.removeItem(lsKey);
        } catch {}
      } else {
        // Try localStorage first
        try {
          localStorage.setItem(lsKey, compressed);
          console.log(
            "[TattooApp] snapshot saved (localStorage) for",
            projectId
          );
          return true;
        } catch (e) {
          warnedLsQuotaRef.current = true;
          console.warn(
            "[TattooApp] snapshot localStorage failed, using IDB",
            e
          );

          // 🔥 critical: delete stale/older LS snapshot so Dashboard won’t reopen it
          try {
            localStorage.removeItem(lsKey);
          } catch {}
        }
      }

      // IDB fallback
      const idbKey = SNAP_IDB_KEY(projectId);
      await idbPut(idbKey, { projectId, payload: compressed });

      console.log("[TattooApp] snapshot saved (IDB) for", projectId);
      return true;
    } catch (e) {
      console.warn("[TattooApp] snapshot save failed", e);
      return false;
    }
  }

  // ---------- THUMBNAIL / RECENTS ----------

  // Capture current preview, store thumb + index in “recent projects”
  async function captureAndIndexPreview(opts = {}) {
    // 🔎 DEBUG MARKER
    console.warn("[TATTUNE-DEBUG] captureAndIndexPreview v2 called", {
      hasName:
        typeof tattoo.projectName === "string" && !!tattoo.projectName.trim(),
      layerCount: Array.isArray(tattoo.layers) ? tattoo.layers.length : -1,
    });
    if (!previewRef.current || !previewRef.current.capturePng) {
      console.warn(
        "[TattooApp] captureAndIndexPreview: no previewRef / capturePng yet"
      );
      return null;
    }

    const projectId = getCurrentProjectId();
    if (!projectId) {
      console.warn(
        "[TattooApp] captureAndIndexPreview: NO projectId yet, skipping index/snapshot"
      );
      return null;
    }

    // 🛡️ Same "empty-empty" guard as autosave — if the user hasn't
    // named the project AND hasn't added any layers yet, don't create
    // a recent entry / snapshot. Otherwise clicking "+ Start a New
    // Session" and immediately going back to Dashboard leaves an
    // orphan "Untitled" / "Tattune" phantom card behind.
    const nameHasContent =
      typeof tattoo.projectName === "string" && tattoo.projectName.trim();
    const hasLayers = Array.isArray(tattoo.layers) && tattoo.layers.length > 0;
    if (!nameHasContent && !hasLayers) {
      console.warn(
        "[TattooApp] captureAndIndexPreview: skipping — empty-empty state (no name, no layers)"
      );
      return null;
    }

    rememberStudioOpened(projectId);

    try {
      try {
        const gp = guidePaintRef?.current;
        if (gp && typeof gp.flush === "function") {
          console.log(
            "[TattooApp] captureAndIndexPreview: flushing GuidePaint"
          );
          gp.flush();
        }
      } catch (e) {
        console.warn("[TattooApp] guidePaint flush failed:", e);
      }

      await new Promise((resolve) => {
        if (typeof requestAnimationFrame !== "undefined") {
          requestAnimationFrame(resolve);
        } else {
          setTimeout(resolve, 16);
        }
      });

      await waitForCompositeReady({ timeoutMs: 1400 });
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(r))
      );

      const dataUrl = await previewRef.current.capturePng({
        thumbnail: true,
        maxWidth: 480,
        format: "image/jpeg",
        quality: 0.7,
      });

      if (!dataUrl || typeof dataUrl !== "string" || dataUrl.length < 1000) {
        console.warn(
          "[TattooApp] captureAndIndexPreview: got empty/short PNG",
          dataUrl && dataUrl.length
        );
        return null;
      }

      const viewSnap = previewRef.current?.getViewSnap?.() || null;
      tattoo.viewState = viewSnap;

      try {
        tattoo.viewSnap = viewSnap;
      } catch {}

      setRenderedImageUrls([dataUrl]);

      try {
        localStorage.setItem("tattoo:last-preview:v1", dataUrl);
      } catch (e) {
        if (!warnedLsQuotaRef.current) {
          warnedLsQuotaRef.current = true;
          console.warn(
            "[TattooApp] localStorage snapshot too big; using IDB for this device."
          );
          cleanupOldSnapshots(3);
        } else {
          console.warn("[TattooApp] failed to store last-preview:", e);
        }
      }

      const projectName =
        (tattoo.projectName && tattoo.projectName.trim()) || "Tattune";

      try {
        const p = upsertRecentProject({
          id: projectId,
          projectName,
          sex: tattoo.sex,
          thumb: dataUrl,
          viewSnap: viewSnap,
        });

        lastThumbWriteRef.current =
          p && typeof p.then === "function" ? p : Promise.resolve();
      } catch (e) {
        console.warn("[TattooApp] failed to update recents:", e);
        lastThumbWriteRef.current = Promise.resolve();
      }

      scheduleSnapshot(projectId);

      return [dataUrl];
    } catch (err) {
      console.warn("[TattooApp] captureAndIndexPreview failed:", err);
      return null;
    }
  }

  // For LeftPanel: refresh preview (and index it)
  async function refreshPreviewImage(opts = {}) {
    const fresh = await captureAndIndexPreview(opts);
    return fresh || renderedImageUrls;
  }

  // Called by PreviewPane when the model/scene is actually ready
  async function captureWhenStable({ maxMs = 2500, stableFrames = 3 } = {}) {
    const start = Date.now();
    let lastSig = null;
    let stable = 0;

    while (Date.now() - start < maxMs) {
      // signature of "what ink should look like"
      let sig = "";
      try {
        const { buildLayersSignature } = await import("./pipeline.js");
        sig = buildLayersSignature(tattoo.layers || []);
      } catch {}

      if (sig && sig === lastSig) stable += 1;
      else stable = 0;

      lastSig = sig;

      if (stable >= stableFrames) break;

      // give GPU + async layer hydration time
      await new Promise((r) =>
        requestAnimationFrame(() => requestAnimationFrame(r))
      );
    }

    await captureAndIndexPreview();
  }

  function handlePreviewReady() {
    console.log(
      "[TattooApp] Preview ready → capturing initial thumbnail (stable)"
    );
    captureWhenStable();
  }
  // if you have / want a placement photo later:
  const placementUrl = null;

  // ------------------- GLOBAL EFFECTS -------------------

  // reflect effective read-only into global state
  useEffect(() => {
    tattoo.readOnly = effectiveReadOnly;
  }, [effectiveReadOnly]);

  // scrub dead blobs once when we’re in an editable context
  useEffect(() => {
    if (readOnly) return;
    try {
      scrubDeadBlobLayers();
    } catch (e) {
      console.warn("[TattooApp] scrubDeadBlobLayers failed:", e);
    }
  }, [readOnly]);

  // block stray drops
  useEffect(() => {
    const onDragOver = (e) => e.preventDefault();
    const onDrop = (e) => e.preventDefault();
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (snapshotTimerRef.current) {
        clearTimeout(snapshotTimerRef.current);
        snapshotTimerRef.current = null;
      }
    };
  }, []);

  // external review-mode (PreviewPane emits tt:review-mode)
  useEffect(() => {
    const onReview = (e) => {
      const on = !!e.detail?.on;
      tattoo.readOnly = on;
    };
    window.addEventListener("tt:review-mode", onReview);
    return () => window.removeEventListener("tt:review-mode", onReview);
  }, []);

  // mirror morph events into state
  useEffect(() => {
    const h = (ev) => {
      const { name, value } = ev.detail || {};
      if (typeof name === "string" && Number.isFinite(value)) {
        setMorph(name, value);
      }
    };
    window.addEventListener("morph:set", h);
    return () => window.removeEventListener("morph:set", h);
  }, []);

  // When autosave / manual save says “just saved”, grab a fresh thumbnail
  useEffect(() => {
    const handler = () => {
      let attempts = 0;

      const tryCapture = () => {
        attempts += 1;

        if (!previewRef.current || !previewRef.current.capturePng) {
          console.warn(
            "[TattooApp] autosave thumb: no previewRef yet (attempt",
            attempts,
            ")"
          );
          if (attempts < 5) {
            setTimeout(tryCapture, 150);
          }
          return;
        }

        requestAnimationFrame(() => {
          captureAndIndexPreview();
        });
      };

      setTimeout(tryCapture, 80);
    };

    window.addEventListener("tattoo:just-saved", handler);
    return () => window.removeEventListener("tattoo:just-saved", handler);
  }, []);

  // Orbit → thumbnail (PreviewPane fires "tattoo:orbit-thumb")
  useEffect(() => {
    let timer = null;

    const onOrbit = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        captureAndIndexPreview();
      }, 250);
    };

    window.addEventListener("tattoo:orbit-thumb", onOrbit);
    return () => {
      window.removeEventListener("tattoo:orbit-thumb", onOrbit);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Listen for "open dashboard" button from PreviewPane / header
  useEffect(() => {
    const openDash = async () => {
      setOpeningDashboard(true); // ✅ SHOW LOADER IMMEDIATELY
      const pid = getCurrentProjectId();

      const isUnsavedLocal =
        typeof pid === "string" && pid.startsWith("local:");

      if (isUnsavedLocal) {
        const ok = window.confirm(
          "This project hasn't been saved as a .tattune file yet.\n\nPress OK to go to the Lounge anyway, or Cancel to stay and save."
        );
        if (!ok) {
          setOpeningDashboard(false); // ✅ user cancelled → hide loader
          return;
        }
      }
      console.log(
        "[TattooApp] dashboard: forcing snapshot before showing dashboard"
      );

      try {
        // 1) capture thumbnail + kick off thumb IDB write
        await captureAndIndexPreview();

        // 2) wait for thumb write to IDB (so Lounge can read it immediately)
        try {
          await lastThumbWriteRef.current;
        } catch {}

        // 3) FORCE snapshot NOW and WAIT for it (so Lounge always reopens latest)
        const pid2 = getCurrentProjectId();

        try {
          await saveSnapshot(pid2); // ✅ deterministic write
          lastSnapshotAtRef.current = Date.now(); // ✅ keep your throttling in sync
        } catch (e) {
          console.warn("[TattooApp] forced snapshot before lounge failed:", e);
        }
      } catch (err) {
        console.warn(
          "[TattooApp] captureAndIndexPreview before dashboard failed:",
          err
        );
      } finally {
        rememberLoungeOpened();
        setTimeout(() => {
          setOpeningDashboard(false);
          setShowDashboard(true);
        }, 120);
      }
    };

    window.addEventListener("tattoo:open-dashboard", openDash);
    return () => window.removeEventListener("tattoo:open-dashboard", openDash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showDashboard) rememberLoungeOpened();
  }, [showDashboard]);
  // ------------------- ROUTING HELPERS -------------------

  const handleBackToStudio = () => {
    try {
      goDesign();
    } catch {}
    try {
      window.location.href = "/app";
    } catch {
      window.location.assign("/app");
    }
  };

  const handleNewSession = () => {
    // 1) FIRST — pin a fresh projectId. This MUST happen before the reset
    // below, because resetProjectToDefaults() blanks projectName but
    // leaves projectId alone. Previously we reset first, which left a
    // brief window where projectId still pointed at the PREVIOUS project
    // while projectName was empty. If an autosave tick landed in that
    // window (async work between valtio proxy sets), it would write
    // {projectName: null} to the previous project's slot, permanently
    // renaming it to the empty-state default. Symptom: "Debbie"
    // spontaneously renaming to "Tattune" after starting a new session.
    const sexLabel = tattoo.sex === "male" ? "male" : "female";
    tattoo.projectId = `local:new:${sexLabel}:${Date.now().toString(36)}`;

    // 2) NOW reset everything else. Any autosave tick that fires from
    // here on will target the NEW slot, not the previous one.
    resetProjectToDefaults({ sex: "female" }); // or pick male if you want
    // ✅ kill any leftover camera locks from previous session (THIS is the knees-view culprit)
    try {
      window.__tt_lock_cam = null;
      window.__tt_cam_lock_until = 0;
      window.__tt_cam_gesture = false;

      // also clear these "camera override" globals if you use them
      window.__tt_recent_viewSnap = null;
      window.__tt_pending_share = null;
      window.__tt_shared_cam_applied = false;
      window.__tt_skip_fit_once = false;
    } catch {}

    // 3) belt-and-suspenders: reset() clears these already, but do it
    // explicitly so intent is obvious at the call site.
    tattoo.projectName = "";
    tattoo.fileName = "";

    // 3) wipe autosave
    try {
      localStorage.removeItem(AUTOSAVE_KEY);
      localStorage.removeItem("tattoo:autosave:v2"); // legacy
      localStorage.removeItem("tattoo:last-preview:v1");
      localStorage.removeItem("tt_pending_flash");
      localStorage.removeItem("tt_pending_request");
    } catch {}

    // ✅ clear any saved "home camera" that might be stuck in knees-view
    try {
      localStorage.removeItem("tt_home_cam__female-fullbody");
      localStorage.removeItem("tt_home_cam__male-fullbody");
    } catch {}

    // 4) tell PreviewPane to hard-reset its camera/controls “one-time apply” gates
    try {
      window.dispatchEvent(new Event("tt:new-session"));
    } catch {}

    // 5) go design
    setShowDashboard(false);
    try {
      goDesign();
    } catch {}
  };

  // ------------------- MAIN APP CHROME -------------------

  const AppChrome = (
    <div
      className={`tattoo-app no-select ${
        isMobile ? "is-mobile" : "is-desktop"
      } theme-dark`}
    >
      <TattooShareInit />

      <main className="tattoo-layout">
        {/* LEFT: sliding design panel */}
        {/* LEFT: mobile bottom sheet / desktop left panel */}
        <section
          ref={isMobile ? sheetRef : null}
          className={`tattoo-left ${snap.leftOpen ? "open" : "closed"} ${
            isMobile ? "tt-sheet" : ""
          }`}
          style={isMobile ? { height: `${sheetH}px` } : undefined}
        >
          {isMobile && (
            <div
              ref={handleRef}
              className="tt-sheet-handle"
              onPointerDown={onSheetPointerDown}
              onPointerMove={onSheetPointerMove}
              onPointerUp={onSheetPointerUp}
              onPointerCancel={onSheetPointerUp}
              role="button"
              aria-label="Drag to resize panel"
            >
              <div className="tt-sheet-grabber" />
            </div>
          )}

          <div className={isMobile ? "tt-sheet-body" : undefined}>
            <LeftPanel
              readOnly={effectiveReadOnly}
              renderedImageUrls={renderedImageUrls}
              placementUrl={placementUrl}
              onExportPreview={exportPreviewImage}
              onExportPreviewVideo={exportPreviewVideo}
              panelMode={panelMode}
              setPanelMode={setPanelMode}
              isMobile={isMobile}
            />
          </div>
        </section>

        {/* RIGHT: header + canvas */}
        <section className="tattoo-right">
          {/* 3D preview */}
          <PreviewPane
            ref={previewRef}
            readOnly={effectiveReadOnly}
            onReady={handlePreviewReady}
            onBackToStudio={handleBackToStudio}
            showBackToStudio={
              isViewerRoute && effectiveReadOnly && !!currentUser
            }
            isMobile={isMobile} // ✅ add
            leftOpen={snap.leftOpen} // ✅ add
            onToggleDesignPanel={toggleDesignPanel}
            panelMode={panelMode} // ✅ add
            setPanelMode={setPanelMode} // ✅ add (optional but handy)
            onOpenMorphs={openMorphs} // ✅ add
            onOpenSheet={() => setSheetOpen(true)}
          />

          {/* Mobile “tab” that pulls the panel up when closed */}
          {isMobile && isSheetClosed && (
            <>
              {/* one-time nudge (viewer-focused) */}
              <FeedbackNudgePortal
                effectiveReadOnly={effectiveReadOnly}
                isMobile={isMobile}
                leftOpen={snap.leftOpen}
              />

              <button
                className="overlay-tab"
                type="button"
                onClick={() => {
                  // open panel
                  // open sheet
                  setSheetOpen();

                  // mark nudge seen
                  try {
                    localStorage.setItem(FEEDBACK_NUDGE_KEY, "1");
                  } catch {}
                }}
                style={{
                  position: "fixed",
                  left: "50%",
                  transform: "translateX(-50%)",
                  bottom: "0px",
                  zIndex: 9999,
                  padding: "10px 18px",
                  borderRadius: "12px 12px 0 0",
                  background: "rgba(40,40,40,0.96)",
                  color: "white",
                  fontSize: "16px",
                  border: "1px solid #444",
                  borderBottom: "none",
                  backdropFilter: "blur(6px)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                {/* label changes in viewer mode */}
                <span>
                  {effectiveReadOnly ? "Feedback ↑" : "Design panel ↑"}
                </span>

                {/* badge (unresolved comment count) */}
                {(() => {
                  const layers = tattoo.layers || [];
                  const count = layers.reduce(
                    (sum, L) => sum + (L?.id ? unresolvedCount(L.id) : 0),
                    0
                  );
                  if (!count) return null;

                  return (
                    <span
                      style={{
                        minWidth: 20,
                        height: 20,
                        padding: "0 6px",
                        borderRadius: 999,
                        background: "#ef4444",
                        color: "white",
                        fontSize: 12,
                        lineHeight: "20px",
                        textAlign: "center",
                        fontVariantNumeric: "tabular-nums",
                      }}
                      aria-label={`${count} new comments`}
                      title={`${count} new comments`}
                    >
                      {count}
                    </span>
                  );
                })()}
              </button>
            </>
          )}

          {/* Floating share (mobile) – only in studio mode */}
          {isMobile && !effectiveReadOnly && (
            <div className="floating-share">
              <SaveShareButton author="Artist" />
            </div>
          )}
        </section>
      </main>
      {openingDashboard && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 999999,
            display: "grid",
            placeItems: "center",
            background: "rgba(10,10,10,0.45)",
            backdropFilter: "blur(3px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(20,20,20,0.92)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
              color: "#eee",
              fontSize: 14,
            }}
          >
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                border: "2px solid rgba(255,255,255,0.22)",
                borderTopColor: "rgba(255,255,255,0.95)",
                animation: "ttspin 0.85s linear infinite",
              }}
            />
            <div>Opening Lounge…</div>
          </div>
        </div>
      )}
      {/* ✅ Merge confirm + undo live at app root so they overlay everything */}
      <MergeConfirmDialog snap={snap} />
      <UndoMergeToast snap={snap} />
    </div>
  );
  {
    requestOpen && requestDraft && (
      <RequestModal
        draft={requestDraft}
        onClose={() => {
          setRequestOpen(false);
          setRequestDraft(null);
        }}
      />
    );
  }
  // In any read-only / viewer context, don't gate behind Auth
  if (effectiveReadOnly) {
    return AppChrome;
  }

  // Normal studio: show Dashboard first, then Studio
  if (allowGuestStudioEntry) {
    return showDashboard ? (
      <div
        style={{
          position: "fixed",
          inset: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          background: "#111",
        }}
      >
        <Dashboard
          onOpenProject={() => {
            const pid = getCurrentProjectId();
            rememberStudioOpened(pid);
            setShowDashboard(false);
            try {
              goDesign();
            } catch {}
          }}
          onNewSession={handleNewSession}
        />
      </div>
    ) : (
      AppChrome
    );
  }

  return (
    <AuthGate>
      {/* Reacts to ?billing=success|cancel on return from Stripe Checkout:
          fires a toast, force-refreshes the subscription hook, and strips
          the query params so reloads don't replay. */}
      <BillingReturnHandler />
      {/* Type tool dialog — mounted once, opened via openTextDialog() */}
      <TextLayerDialog
        getLayer={(id) => (tattoo.layers || []).find((l) => l.id === id)}
        onAdd={addTextLayer}
        onUpdate={updateTextLayer}
      />
      {showDashboard ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            background: "#111",
          }}
        >
          <Dashboard
            onOpenProject={() => {
              const pid = getCurrentProjectId();
              rememberStudioOpened(pid);
              setShowDashboard(false);
              try {
                goDesign();
              } catch {}
            }}
            onNewSession={handleNewSession}
          />
        </div>
      ) : (
        AppChrome
      )}
    </AuthGate>
  );
}
